import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { billingGet, billingSend, stripeHostUrl } from '../api/billingClient';

const BillingContext = createContext(null);

function currentSubscription(subscription) {
  return subscription && ['active', 'trialing'].includes(subscription.status) ? subscription : null;
}

export function BillingProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [showPlans, setShowPlans] = useState(true);
  const [notice, setNotice] = useState('');

  const reload = useCallback(async () => {
    const [subscriptionResult, plansResult] = await Promise.all([
      billingGet('/subscription'),
      billingGet('/plans'),
    ]);
    if (subscriptionResult.ok) setSubscription(subscriptionResult.body.subscription || null);
    if (plansResult.ok) setShowPlans(plansResult.body.showPlans !== false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!checkout) return undefined;
    window.history.replaceState(null, '', window.location.pathname);
    if (checkout === 'cancel') {
      setNotice('cancel');
      return undefined;
    }
    if (checkout !== 'success' || !sessionId) return undefined;
    let cancel = false;
    setNotice('working');
    billingSend('/confirm', { sessionId }).then((result) => {
      if (cancel) return;
      setNotice(result.ok ? 'active' : 'error');
      if (result.ok) reload();
    });
    return () => {
      cancel = true;
    };
  }, [reload]);

  const startCheckout = useCallback(async (planId) => {
    const result = await billingSend('/checkout', { planId });
    if (result.ok && stripeHostUrl(result.body.url, 'checkout.stripe.com')) {
      window.location.assign(result.body.url);
      return { ok: true };
    }
    return result;
  }, []);

  const openPortal = useCallback(async () => {
    const result = await billingSend('/portal', {});
    if (result.ok && stripeHostUrl(result.body.url, 'billing.stripe.com')) {
      window.location.assign(result.body.url);
      return { ok: true };
    }
    return result;
  }, []);

  const value = useMemo(() => ({
    subscription,
    pro: currentSubscription(subscription),
    showPlans,
    notice,
    clearNotice: () => setNotice(''),
    startCheckout,
    openPortal,
  }), [subscription, showPlans, notice, startCheckout, openPortal]);

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  return useContext(BillingContext);
}
